import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { isPendingTask, type RowPatch, type TaskSummary } from '../../lib/tasks';
import { nextTaskStatus, TASK_STATUS_LABELS } from '../../lib/taskStatus';
import { nextTaskPriority, TASK_PRIORITY_LABELS } from '../../lib/taskPriority';
import { formatDuration } from '../../lib/duration';
import { reminderBadge } from '../../lib/reminders';
import { describeRepeat } from '../../lib/recurrence';
import { TASK_GRID } from './taskGrid';
import { AvatarStack, PriorityDot } from './TaskBadges';
import { CHECKBOX_BOX, FOCUS_RING_FLUSH } from '../ui';
import { DueDateCell } from './DueDatePicker';
import { StatusCell } from './StatusPicker';
import { useRevealOnFlash } from './useRowFlash';
import {
  ArrowRightIcon,
  BellIcon,
  BlockedIcon,
  CheckIcon,
  ChecklistIcon,
  ClockIcon,
  CommentIcon,
  PaperclipIcon,
  RepeatIcon,
  SubtaskIcon,
} from '../icons';

/**
 * One task, laid out on the shared column template. The `<Link>` fills the row
 * via `after:absolute after:inset-0`, and every control sits outside it at
 * `relative z-10` so its click does not also follow the link -- the same
 * arrangement PinButton uses inside ProjectRow.
 *
 * Most of the row is editable in place: the due date opens a picker, the
 * priority escalates on click, and two buttons at the trailing edge advance the
 * status and mark the task done. None of them owns a request. A status change
 * moves the row into a different `<section>` and unmounts it, so a reply it was
 * waiting on would have nowhere to land; the page holds every request instead
 * and this only reports the intent.
 *
 * The circle on the left selects the row. It used to mark the task done, which
 * is now the ✓ button -- the recolour from emerald to honey is what stops it
 * still reading as a completion tick. Completion is still legible from the
 * struck-through title and the Done group it sits in.
 *
 * A row can also be a task the server has not confirmed yet (see
 * `optimisticTask`). Those render as the same row without the link or any of
 * the controls: there is no id to open or to PATCH for about a second, and a row
 * that looks live but answers "Task not found" is worse than one that plainly
 * says it is still saving.
 *
 * `showProject` adds a project column, for the cross-project Tasks page. Note
 * that DayTaskRow made the opposite call and is a sibling of this component
 * rather than a mode of it -- correctly, because the dashboard card *swaps* the
 * counter cluster for a project name and shows four columns in total. This page
 * wants every column here and one more, so the alternative was a copy of the
 * ~300 lines of inline editing below. A column that renders or does not is the
 * cheaper seam.
 */
export function TaskRow({
  task,
  parentTitle,
  href,
  active,
  selected,
  flashing = false,
  grid = TASK_GRID,
  showProject = false,
  onToggleSelected,
  onPatch,
}: {
  task: TaskSummary;
  parentTitle: string | null;
  href: string;
  /** Must be the template the whole list is drawn on -- see taskGrid.ts. */
  grid?: string;
  showProject?: boolean;
  /** The task whose panel is open behind the list. Not the same as `selected`:
   *  both can be true, and they are shown differently. */
  active: boolean;
  selected: boolean;
  /** Its due date was just edited and the list moved it -- see useRowFlash. */
  flashing?: boolean;
  /** `range` is a shift-click -- everything between the last toggle and this. */
  onToggleSelected: (options: { range: boolean }) => void;
  onPatch: (patch: RowPatch) => void;
}) {
  const pending = isPendingTask(task.id);
  const done = task.status === 'DONE';
  const blocked = task.incompletePrerequisiteCount > 0 && !done;
  const reminder = reminderBadge(task);
  const next = nextTaskStatus(task.status);
  const escalated = nextTaskPriority(task.priority);
  const rowRef = useRef<HTMLLIElement>(null);
  useRevealOnFlash(rowRef, flashing);

  /*
   * Reads the status and changes it to any of the five, which the two buttons
   * at the trailing edge cannot: they are one click each for the two moves that
   * dominate, and this is everything else without opening the task.
   *
   * Worth a column even though the list is grouped by status by default: the
   * grouping can be turned off, and once it is, the group heading was the only
   * thing on the page saying what state anything was in.
   *
   * Held in a variable because it renders in one of two places -- right after
   * the title on the Tasks page, right after the assignees everywhere else.
   * Both templates in taskGrid.ts spell out why; what matters here is that it
   * is built once, so the two call sites cannot drift apart.
   */
  const statusCell = (
    <StatusCell
      status={task.status}
      taskTitle={task.title}
      disabled={pending}
      onPick={(status) => onPatch({ status })}
    />
  );

  return (
    <li
      ref={rowRef}
      /*
       * Three states, three appearances -- which they did not have. `selected`
       * used to paint `bg-slate-50` and hover painted `bg-slate-50` too, so a
       * selected row was indistinguishable from one the pointer happened to be
       * over; and because they were branches of one ternary, a selected or open
       * row got no hover feedback at all.
       *
       * They are separate now: hover is a warm wash, selection is a honey wash
       * with a rail down its left edge, and the open row is the same wash with
       * a ring around it. Hover composes on top of all three.
       *
       * The open row is a ring and not a heavier fill because it spends most of
       * its life underneath the panel's scrim, and a strong honey read through
       * 40% black as a muddy olive band -- the loudest thing on a screen you are
       * not even looking at.
       */
      className={`${grid} group relative px-2 py-1 transition hover:bg-stone-100/70 ${
        active
          ? 'bg-honey-50 ring-1 ring-inset ring-honey-300'
          : selected
            ? 'bg-honey-50'
            : ''
      } ${flashing ? 'animate-row-flash' : ''} ${
        // The `<li>` is already `relative`, so the bar costs nothing extra. It
        // is what keeps selection distinguishable on the open task, where the
        // honey wash of `active` is already covering the row.
        selected
          ? 'before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-honey-500'
          : ''
      }`}
    >
      <button
        type="button"
        onClick={(event) => onToggleSelected({ range: event.shiftKey })}
        disabled={pending}
        // `role="checkbox"`, not `aria-pressed`: this is membership of a
        // selection, not a control that toggles a property of the task. A real
        // `<input type="checkbox">` would be the honest element, but there is no
        // forms plugin here, so it would render as unstyled platform chrome in
        // the middle of the row.
        role="checkbox"
        aria-checked={selected}
        aria-label={selected ? `Deselect ${task.title}` : `Select ${task.title}`}
        // CHECKBOX_BOX carries the geometry -- see the note on it for why the
        // hit area is grown by a pseudo-element. `z-10` is added here because
        // this one sits over the row's full-bleed link and has to take the click
        // before it rather than fight it.
        className={`z-10 ${CHECKBOX_BOX} disabled:cursor-default ${
          selected
            ? 'border-honey-500 bg-honey-500 text-white'
            : 'border-stone-300 text-transparent hover:border-stone-400 hover:text-stone-400'
        }`}
      >
        <CheckIcon className="h-2.5 w-2.5" />
      </button>

      <span className="flex min-w-0 items-center gap-1.5">
        {parentTitle ? (
          // A marker rather than a second line: a subtask can sit in a different
          // status group from its parent, so the tree and the grouping cannot
          // both be shown at once. The parent's name is on the tooltip.
          <SubtaskIcon className="h-3 w-3 shrink-0 text-stone-400" />
        ) : null}

        {pending ? (
          <span className="truncate text-dense text-stone-500" title={task.title}>
            {task.title}
          </span>
        ) : (
          <Link
            to={href}
            title={parentTitle ? `${task.title} — subtask of ${parentTitle}` : task.title}
            className={`truncate rounded text-dense after:absolute after:inset-0 ${FOCUS_RING_FLUSH} ${
              done ? 'text-stone-500 line-through' : 'text-stone-900'
            }`}
          >
            {task.title}
          </Link>
        )}

        <span className="flex shrink-0 items-center gap-1.5 text-meta text-stone-500">
          {/* Says what the greyed-out row means. The counts below it are all
              zero on a task this new, so nothing else is competing for the
              space. */}
          {pending ? <span>Saving…</span> : null}

          {blocked ? (
            <span
              className="flex items-center gap-0.5 text-amber-600"
              title={`Waiting on ${task.incompletePrerequisiteCount} unfinished prerequisite${
                task.incompletePrerequisiteCount === 1 ? '' : 's'
              }`}
            >
              <BlockedIcon className="h-3 w-3" />
              {task.incompletePrerequisiteCount}
            </span>
          ) : null}

          {task.subtaskCount > 0 ? (
            <span className="flex items-center gap-0.5" title="Subtasks">
              <SubtaskIcon className="h-3 w-3" />
              {task.subtaskCount}
            </span>
          ) : null}

          {task.commentCount > 0 ? (
            <span className="flex items-center gap-0.5" title="Comments">
              <CommentIcon className="h-3 w-3" />
              {task.commentCount}
            </span>
          ) : null}

          {task.attachmentCount > 0 ? (
            <span className="flex items-center gap-0.5" title="Attachments">
              <PaperclipIcon className="h-3 w-3" />
              {task.attachmentCount}
            </span>
          ) : null}

          {task.checklistCount > 0 ? (
            // Both numbers, unlike every other badge on this strip. "3" alone
            // would read as a quantity of things, when what it means is
            // progress -- and a checklist with nothing ticked would look
            // identical to one that is finished.
            <span
              className="flex items-center gap-0.5 tabular-nums"
              title={`Checklist: ${task.checklistDoneCount} of ${task.checklistCount} done`}
            >
              <ChecklistIcon className="h-3 w-3" />
              {task.checklistDoneCount}/{task.checklistCount}
            </span>
          ) : null}

          {task.trackedSeconds > 0 ? (
            <span className="flex items-center gap-0.5" title="Time logged">
              <ClockIcon className="h-3 w-3" />
              {formatDuration(task.trackedSeconds)}
            </span>
          ) : null}

          {/* A reminder still ahead of us. The only badge on this strip showing
              something that has not happened yet, which is why it goes once the
              moment passes rather than sitting there as a record -- a bell on a
              task nothing is going to ring about is a lie. The time and not a
              count, because there is only ever one. */}
          {reminder ? (
            <span className="flex items-center gap-0.5 tabular-nums" title={reminder.title}>
              <BellIcon className="h-3 w-3" />
              {reminder.clock}
            </span>
          ) : null}

          {/* An icon alone: the rule is a phrase, and the strip has no room
              for one. The tooltip carries it. */}
          {task.repeat ? (
            <span className="flex items-center" title={`Repeats: ${describeRepeat(task.repeat)}`}>
              <RepeatIcon className="h-3 w-3" />
              <span className="sr-only">Repeats {describeRepeat(task.repeat)}</span>
            </span>
          ) : null}
        </span>
      </span>

      {/* Ahead of the project on this page only, in the track
          TASK_GRID_WITH_PROJECT opens for it. */}
      {showProject ? statusCell : null}

      {/* Its own link, so the project is a way into it rather than just a
          caption -- hence `z-10` to win against the row link above, the same
          arrangement DayTaskRow uses. Hidden below `sm:` along with the due and
          priority columns the template drops there. */}
      {showProject ? (
        <Link
          to={`/projects/${task.project.id}/tasks`}
          title={task.project.name}
          className={`relative z-10 hidden truncate rounded text-meta text-stone-500 transition hover:text-stone-900 hover:underline sm:block ${FOCUS_RING_FLUSH}`}
        >
          {task.project.name}
        </Link>
      ) : null}

      {/* Wrapped because the stack renders nothing when nobody is on the task,
          and a missing child does not hold its grid track open -- unassigned
          rows would pull the due and priority columns one track left. */}
      <span>
        {/* Owner first: they are the one accountable, so they should be the
            face that survives the cap. */}
        <AvatarStack
          people={[...(task.owner ? [task.owner] : []), ...task.collaborators]}
        />
      </span>

      {/* Where it sits without a project column: straight after the avatars and
          ahead of the dates, the pair of tracks it used to trail. */}
      {showProject ? null : statusCell}

      <DueDateCell task={task} disabled={pending} onPick={(dueDate) => onPatch({ dueDate })} />

      {/* `flex`, not `block`: a block wrapper gives the inline dot a line box
          of the inherited 1.5rem line-height, which was the tallest thing in
          the row and set the row's height on its own. */}
      <button
        type="button"
        disabled={pending}
        onClick={() => onPatch({ priority: escalated })}
        // Names the destination, so nobody has to click once to find out which
        // way the cycle runs.
        title={`Priority: ${TASK_PRIORITY_LABELS[task.priority]} — click for ${
          TASK_PRIORITY_LABELS[escalated]
        }`}
        aria-label={`Priority ${TASK_PRIORITY_LABELS[task.priority]}. Change to ${
          TASK_PRIORITY_LABELS[escalated]
        }`}
        // Two-stage reveal, the same idea as TaskComposer's expand button: at
        // rest the cell is pixel-identical to the label it replaced, because a
        // permanently boxed control on every row turns the list into a form.
        // Hovering the row draws a hairline to say it is a control at all;
        // hovering the cell fills it.
        className={`relative z-10 -mx-1 hidden rounded px-1 py-0.5 text-left transition hover:bg-stone-200/70 group-hover:ring-1 group-hover:ring-inset group-hover:ring-stone-300 disabled:cursor-default disabled:hover:bg-transparent disabled:group-hover:ring-0 sm:flex ${FOCUS_RING_FLUSH}`}
      >
        <PriorityDot priority={task.priority} label />
      </button>

      {/*
        The row's actions. Wrapped in one span so the track keeps its shape on a
        Done row, where the advance button is gone.

        `invisible`, never `opacity-0`: an `opacity-0` element still hit-tests,
        and these sit inside a row whose `<Link>` covers the whole area, so an
        invisible-but-clickable button at the trailing edge would silently eat
        clicks meant to open the task. (TaskComposer can use `opacity-0` because
        its row has no link overlay.)

        Only hidden from `sm:` up. Below it there is no hover to reveal them
        with -- and the due and priority cells are gone at that width, so these
        are the only inline edits a phone has.

        Keyboard reach is indirect, and looks like a bug if you reason about it
        statically: `visibility: hidden` takes an element out of the tab order,
        so these cannot reveal themselves by being focused. Tab to the row's
        title instead, the `<li>` matches `:focus-within`, they become visible,
        and the next Tab lands on them.
      */}
      <span className="flex items-center justify-end gap-0.5">
        {pending ? null : (
          <>
            {next ? (
              <button
                type="button"
                onClick={() => onPatch({ status: next })}
                title={`Move to ${TASK_STATUS_LABELS[next]}`}
                aria-label={`Move ${task.title} to ${TASK_STATUS_LABELS[next]}`}
                className={`relative z-10 flex h-5 w-5 items-center justify-center rounded text-stone-500 transition after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-[''] hover:bg-stone-200/70 hover:text-stone-900 sm:invisible sm:group-hover:visible sm:group-focus-within:visible ${FOCUS_RING_FLUSH}`}
              >
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </button>
            ) : null}

            <button
              type="button"
              // Reopens to TODO from Done, which is exactly what the circle used
              // to do -- the one-click undo survives the circle's new job.
              onClick={() => onPatch({ status: done ? 'TODO' : 'DONE' })}
              title={done ? 'Reopen' : 'Mark done'}
              aria-label={done ? `Reopen ${task.title}` : `Mark ${task.title} done`}
              className={`relative z-10 flex h-5 w-5 items-center justify-center rounded transition after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-[''] sm:invisible sm:group-hover:visible sm:group-focus-within:visible ${FOCUS_RING_FLUSH} ${
                done
                  ? 'text-emerald-600 hover:bg-emerald-50'
                  : 'text-stone-500 hover:bg-emerald-50 hover:text-emerald-600'
              }`}
            >
              <CheckIcon className="h-3.5 w-3.5" />
            </button>
          </>
        )}
      </span>
    </li>
  );
}
